'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { downloadCSV, buildBookingReport, buildLeadReport } from '@/lib/emailReportExport';
import { useState } from 'react';

const DataBackupsAdmin = () => {
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleExport = (type: 'bookings' | 'leads') => {
    setDownloading(type);
    setTimeout(() => {
      const report = type === 'bookings' ? buildBookingReport() : buildLeadReport();
      downloadCSV(report);
      setDownloading(null);
    }, 800);
  };

  const quickExports = [
    { id: 'bookings', label: 'Bookings Export', icon: 'TicketIcon', color: 'text-blue-600', bg: 'bg-blue-50', records: '247 records', size: '~142 KB' },
    { id: 'leads', label: 'Leads Export', icon: 'UserGroupIcon', color: 'text-purple-600', bg: 'bg-purple-50', records: '523 records', size: '~89 KB' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground">Data Backups</h2>
          <p className="text-sm text-muted-foreground mt-1">Export and schedule automated data backups</p>
        </div>
        <Link
          href="/data-backups"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-colors"
        >
          <Icon name="ArrowTopRightOnSquareIcon" size={16} />
          Full Backup Manager
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quickExports.map(exp => (
          <div key={exp.id} className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${exp.bg}`}>
                <Icon name={exp.icon as any} size={20} className={exp.color} />
              </div>
              <div>
                <div className="font-bold text-foreground">{exp.label}</div>
                <div className="text-xs text-muted-foreground">{exp.records} · {exp.size}</div>
              </div>
            </div>
            <button
              onClick={() => handleExport(exp.id as 'bookings' | 'leads')}
              disabled={downloading === exp.id}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              <Icon name={downloading === exp.id ? 'ArrowPathIcon' : 'ArrowDownTrayIcon'} size={16} className={downloading === exp.id ? 'animate-spin' : ''} />
              {downloading === exp.id ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        ))}
      </div>
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex items-center gap-3 mb-3">
          <Icon name="CalendarDaysIcon" size={18} className="text-primary" />
          <span className="font-bold text-foreground">Scheduled Jobs</span>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Daily Bookings Export', schedule: 'Daily', next: 'In 8h', status: 'active' },
            { name: 'Weekly Lead Report', schedule: 'Weekly', next: 'In 3d', status: 'active' },
            { name: 'Monthly Analytics', schedule: 'Monthly', next: 'In 12d', status: 'active' },
          ].map((job, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-sm font-medium text-foreground">{job.name}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{job.schedule}</span>
                <span className="text-xs font-bold text-primary">{job.next}</span>
                <span className="w-2 h-2 rounded-full bg-green-500" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataBackupsAdmin;
